import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Legacy translation dictionary — used by pages that still rely on t('key').
// New code should use the structured i18n tree via `useT()` from `/src/i18n`.
// Only NO and EN are supported (Tagalog/Albanian have been removed).
const translations = {
  no: {
    features: 'Funksjoner',
    howItWorks: 'Slik fungerer det',
    findJobs: 'Finn Jobber',
    findTalent: 'Finn Talent',
    login: 'Logg Inn',
    register: 'Registrer Deg',

    heroTitle: 'Din drøm om å jobbe i Norge starter her',
    heroSubtitle: 'Vi kobler kvalifiserte fagarbeidere med ledende kommuner i Norge. Oppdag muligheter, bygg karriere, og opplev livet i et av verdens mest attraktive land.',
    getStartedPro: 'Jeg søker jobb',
    getStartedEmp: 'Jeg ansetter',

    benefitFree: 'Gratis å bruke',
    benefitSecure: 'Trygt & sikkert',
    benefitAI: 'AI-drevet matching',

    journeyTitle: 'Din reise til Norge',
    journeySubtitle: 'Vi følger deg hele veien fra søknad til etablering',

    journeyStep1Title: 'Søk & Match',
    journeyStep1Desc: 'Opprett profil og finn jobber tilpasset dine kvalifikasjoner',
    journeyStep2Title: 'Intervju & Tilbud',
    journeyStep2Desc: 'Vi hjelper deg forberede intervju og forhandle arbeidskontrakt',
    journeyStep3Title: 'Godkjenning & Visum',
    journeyStep3Desc: 'Støtte med autorisasjon fra Helsedirektoratet og visumsøknad',
    journeyStep4Title: 'Flytting & Bosetting',
    journeyStep4Desc: 'Hjelp med bolig, bank, språkkurs og praktiske gjøremål',
    journeyStep5Title: 'Integrering',
    journeyStep5Desc: 'Kontinuerlig oppfølging og støtte i din nye hverdag i Norge',

    activeJobs: 'Aktive Stillinger',
    registeredProfessionals: 'Registrerte Fagarbeidere',
    employers: 'Kommuner',
    satisfiedUsers: 'Fornøyde Brukere',

    featuresTitle: 'Hvorfor Velge Norvig?',

    roiTitle: 'Hva sparer du?',
    roiSubtitle: 'Se hva du sparer ved å rekruttere direkte vs bemanningsbyrå',
    roiNumEmployees: 'Antall ansatte',
    roiSalaryLabel: 'Årlig lønn per ansatt',
    roiDirectCost: 'Kostnad via Norvig',
    roiAgencyCost: 'Kostnad via bemanningsbyrå',
    roiSavings: 'Din besparelse',
    roiPerYear: 'per år',
    roiFreeLabel: 'Gratis',
    roiCalculate: 'Beregn besparelse',
    roiAgencyFee: 'Bemanningsbyrå tar typisk 20-30% av årlig lønn',
    roiStarCareInfo: 'Gratis å komme i gang — åpen sektorsats kun ved ansettelse',

    forMunicipalities: 'For Kommuner',
    municipalitiesHero: 'Rekrutter fagarbeidere uten mellommann',
    municipalitiesSubtitle: 'Spar millioner og få direkte tilgang til kvalifiserte kandidater',

    langNorwegian: 'Norsk',
    langEnglish: 'English',
  },

  en: {
    features: 'Features',
    howItWorks: 'How it works',
    findJobs: 'Find Jobs',
    findTalent: 'Find Talent',
    login: 'Log in',
    register: 'Sign up',

    heroTitle: 'Your dream of working in Norway starts here',
    heroSubtitle: 'We connect skilled workers with leading employers in Norway. Discover opportunities, build careers, and experience life in one of the world\'s most attractive countries.',
    getStartedPro: 'I am looking for a job',
    getStartedEmp: 'I am hiring',

    benefitFree: 'Free to use',
    benefitSecure: 'Safe & secure',
    benefitAI: 'AI-driven matching',

    journeyTitle: 'Your Journey to Norway',
    journeySubtitle: 'We support you every step of the way from application to settlement',

    journeyStep1Title: 'Apply & Match',
    journeyStep1Desc: 'Create your profile and find jobs matching your qualifications',
    journeyStep2Title: 'Interview & Offer',
    journeyStep2Desc: 'We help you prepare for interviews and negotiate employment contracts',
    journeyStep3Title: 'Authorization & Visa',
    journeyStep3Desc: 'Support with health authority authorization and visa application',
    journeyStep4Title: 'Relocation & Settlement',
    journeyStep4Desc: 'Help with housing, banking, language courses and practical matters',
    journeyStep5Title: 'Integration',
    journeyStep5Desc: 'Continuous follow-up and support in your new life in Norway',

    activeJobs: 'Active Jobs',
    registeredProfessionals: 'Registered Workers',
    employers: 'Employers',
    satisfiedUsers: 'Satisfied Users',

    featuresTitle: 'Why Choose Norvig?',

    roiTitle: 'What Do You Save?',
    roiSubtitle: 'See what you save by recruiting directly vs staffing agency',
    roiNumEmployees: 'Number of employees',
    roiSalaryLabel: 'Annual salary per employee',
    roiDirectCost: 'Cost via Norvig',
    roiAgencyCost: 'Cost via staffing agency',
    roiSavings: 'Your savings',
    roiPerYear: 'per year',
    roiFreeLabel: 'Free',
    roiCalculate: 'Calculate savings',
    roiAgencyFee: 'Staffing agencies typically charge 20-30% of annual salary',
    roiStarCareInfo: 'Free to get started — open sector rate only on hire',

    forMunicipalities: 'For Municipalities',
    municipalitiesHero: 'Recruit skilled workers without a middleman',
    municipalitiesSubtitle: 'Save millions and gain direct access to qualified candidates',

    langNorwegian: 'Norsk',
    langEnglish: 'English',
  },
};

export const LanguageProvider = ({ children }) => {
  // Capture, on first render only, whether we had to auto-detect to EN.
  // This is used by <LangAutoDetectToast /> to decide if it should appear.
  const autoDetectedToEnRef = React.useRef(false);

  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('starcare_language');
    if (stored) return stored;

    // Auto-detect from browser language on first visit.
    // Norwegian variants (nb, nn, no) → 'no'; everything else → 'en'.
    const browserLang = (
      (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) || 'no'
    ).toLowerCase();
    if (
      browserLang.startsWith('nb') ||
      browserLang.startsWith('nn') ||
      browserLang.startsWith('no')
    ) {
      return 'no';
    }
    autoDetectedToEnRef.current = true;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('starcare_language', language);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language === 'en' ? 'en' : 'no';
    }
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const changeLanguage = (lang) => {
    if (['no', 'en'].includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, autoDetectedToEn: autoDetectedToEnRef.current }}>
      {children}
    </LanguageContext.Provider>
  );
};
